import React, { useState } from 'react';
import {
  Box,
  Paper,
  TextField,
  Button,
  Typography,
  MenuItem,
  CircularProgress,
  Alert,
  useTheme,
  useMediaQuery,
  Grid,
  Card,
  CardContent
} from '@mui/material';
import {
  Poll,
  Send,
  CheckCircle
} from '@mui/icons-material';
import { baseUrl, config, requestID } from '../common/utils';
import axios from 'axios';
import { IonContent, IonPage } from '@ionic/react';

const SurveyForm = ({ token }) => {
  const [formData, setFormData] = useState({
    category: '',
    score: ''
  });
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [showSuccess, setShowSuccess] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const configWithAuth = {
    ...config,
    headers: {
      ...config.headers,
      'Authorization': `Bearer ${token}`
    }
  };

  const categories = [
    { value: 'communication', label: 'Communication' },
    { value: 'technical', label: 'Technical Skills' },
    { value: 'teamwork', label: 'Teamwork' },
    { value: 'leadership', label: 'Leadership' },
    { value: 'productivity', label: 'Productivity' },
    { value: 'quality', label: 'Quality of Work' },
    { value: 'initiative', label: 'Initiative' },
    { value: 'adaptability', label: 'Adaptability' }
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage('');
    setShowSuccess(false);

    try {
      const payload = { 
        requestID: requestID(),
        ...formData
      };
      
      const res = await axios.post(
        `${baseUrl()}survey/create`,
        payload,
        configWithAuth
      );

      if (res.data.success) {
        setMessage('Survey submitted successfully!');
        setShowSuccess(true);
        setFormData({ category: '', score: '' });
        
        setTimeout(() => setShowSuccess(false), 3000);
      }
    } catch (error) {
      setMessage('Failed to submit survey. Please try again.');
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <IonPage>
      <IonContent>
        <Box sx={{ p: isMobile ? 2 : 3, maxWidth: 800, mx: 'auto' }}>
          <Typography variant="h4" gutterBottom sx={{ 
            fontWeight: 600, 
            color: 'primary.main',
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            mb: 3
          }}>
            <Poll /> Performance Survey
          </Typography>

          <Grid container spacing={3}>
            <Grid item xs={12} md={8}>
              <Paper elevation={2} sx={{ p: 3, borderRadius: 2 }}>
                {showSuccess && (
                  <Alert 
                    severity="success" 
                    icon={<CheckCircle />}
                    sx={{ mb: 3 }}
                    onClose={() => setShowSuccess(false)}
                  >
                    {message}
                  </Alert>
                )}

                {message && !showSuccess && (
                  <Alert 
                    severity="error" 
                    sx={{ mb: 3 }}
                    onClose={() => setMessage('')}
                  >
                    {message}
                  </Alert>
                )}

                <form onSubmit={handleSubmit}>
                  <TextField
                    select
                    fullWidth
                    label="Category"
                    name="category"
                    value={formData.category}
                    onChange={handleChange}
                    required
                    variant="outlined"
                    sx={{ mb: 3 }}
                  >
                    {categories.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </TextField>

                  <TextField
                    fullWidth
                    label="Score (1-10)"
                    name="score"
                    type="number"
                    value={formData.score}
                    onChange={handleChange}
                    required
                    variant="outlined"
                    inputProps={{ min: 1, max: 10 }}
                    sx={{ mb: 3 }}
                  />

                  <Button
                    type="submit"
                    variant="contained"
                    size="large"
                    disabled={loading}
                    fullWidth
                    sx={{
                      borderRadius: 2,
                      py: 1.5,
                      fontSize: '1.1rem',
                      fontWeight: 600
                    }}
                    startIcon={loading ? <CircularProgress size={20} /> : <Send />}
                  >
                    {loading ? 'Submitting...' : 'Submit Survey'}
                  </Button>
                </form>
              </Paper>
            </Grid>

            <Grid item xs={12} md={4}>
              <Card sx={{ borderRadius: 2 }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom color="primary">
                    📊 Rating Guide
                  </Typography>
                  <Box sx={{ pl: 1 }}>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      <strong>10:</strong> Exceptional
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      <strong>8-9:</strong> Excellent
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      <strong>6-7:</strong> Good
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      <strong>4-5:</strong> Average
                    </Typography>
                    <Typography variant="body2">
                      <strong>1-3:</strong> Needs Improvement
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Box>
      </IonContent>
    </IonPage>
  );
};

export default SurveyForm;